import { createSlice, PayloadAction, nanoid, createAsyncThunk } from "@reduxjs/toolkit";
import {
  IChatState,
  WidgetDisplayState,
  NewAgentMessageProps,
  PythonInterpreterToolName,
  AgentMessage,
  ISessionState,
} from "./chatModels";
import { convertServerChartToClientFormat } from "./utils/chartUtils";
import * as api from "./chatApi";

const sessionInitialState: ISessionState = {
  conversationId: undefined,
  messages: { status: "loaded", value: [] },
  isSessionLocked: false,
  partialResponse: "",
  partialReasoning: "",
  isAgentReplyInProgress: false,
  toolCallInProgress: { status: false },
  reasoningSummaryIndex: -1,
};

const initialState: IChatState = {
  displayState: "closed",
  isFullScreen: true,
  conversationsHistory: { status: "loaded", value: [] },
  isSideBarOpen: false,
  session: sessionInitialState,
};

const errorMessages = {
  unhandledError:
    "Sorry, something went wrong while processing your request. Please try again or contact support if the problem persists.",
  tooManyRequests: "This conversation has reached its messages limit. Please start a new conversation.",
};
export const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    postUserMessage: (state, action: PayloadAction<string>) => {
      if (state.session.messages.status !== "loaded") {
        console.error("Cannot add user message when messages is in error or loading state");
        return;
      }
      if (state.session.isSessionLocked) {
        console.error("Cannot add user message when session is locked");
        return;
      }
      if (state.session.isAgentReplyInProgress) {
        console.error("Cannot add user message when agent reply is in progress");
        return;
      }
      state.session.messages.value.push({
        id: nanoid(),
        origin: "user",
        content: action.payload,
        timestamp: Date.now(),
        messageType: "text",
      });
      state.session.isAgentReplyInProgress = true;
    },

    setSideBarOpen: (state, action: PayloadAction<boolean>) => {
      state.isSideBarOpen = action.payload;
    },

    setDisplayState: (state, action: PayloadAction<WidgetDisplayState>) => {
      state.displayState = action.payload;
    },

    setIsFullScreen: (state, action: PayloadAction<boolean>) => {
      state.isFullScreen = action.payload;
    },

    resetSession: (state) => {
      state.session = { ...sessionInitialState };
      delete state.conversationHistoryRequestId;
    },

    handleAgentMessage: (state, action: PayloadAction<NewAgentMessageProps>) => {
      if (
        action.payload.conversationId &&
        state.session.conversationId != action.payload.conversationId
      ) {
        console.warn("Ignoring server event for different conversation:", action.payload);
        return;
      }
      return handleAgentMessageImpl(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getConversationHistory.fulfilled, (state, action) => {
        if (state.conversationHistoryRequestId !== action.meta.requestId) {
          return;
        }
        delete state.conversationHistoryRequestId;
        const { conversationId, ...history } = action.payload;
        state.session.conversationId = conversationId;
        state.session.messages = { status: "loaded", value: [] };
        for (const msg of history.messages) {
          if (msg.content.type === "user_message") {
            state.session.messages.value.push({
              id: nanoid(),
              origin: "user",
              content: msg.content.content,
              timestamp: Date.parse(msg.timestamp),
              messageType: "text",
            });
          } else {
            handleAgentMessageImpl(state, {
              conversationId,
              message: msg.content,
              timestamp: Date.parse(msg.timestamp),
            });
          }
        }
      })
      .addCase(getConversationHistory.rejected, (state, action) => {
        if (state.conversationHistoryRequestId !== action.meta.requestId) {
          return;
        }
        delete state.conversationHistoryRequestId;
        if (action.meta.aborted) {
          state.session = { ...sessionInitialState };
          return;
        }
        state.session.messages = { status: "error" };
      })
      .addCase(getConversationHistory.pending, (state, action) => {
        state.conversationHistoryRequestId = action.meta.requestId;
        state.session = { ...sessionInitialState, messages: { status: "loading" } };
      })

      .addCase(getConversationsList.fulfilled, (state, action) => {
        if (state.conversationListRequestId !== action.meta.requestId) {
          return;
        }
        delete state.conversationListRequestId;
        state.conversationsHistory = { status: "loaded", value: action.payload };
      })
      .addCase(getConversationsList.rejected, (state, action) => {
        if (state.conversationListRequestId !== action.meta.requestId) {
          return;
        }
        delete state.conversationListRequestId;
        state.conversationsHistory = { status: "error" };
      })
      .addCase(getConversationsList.pending, (state, action) => {
        state.conversationListRequestId = action.meta.requestId;
        state.conversationsHistory = { status: "loading" };
      })

      .addCase(deleteConversation.fulfilled, (state, action) => {
        state.session = { ...sessionInitialState };
        state.conversationsHistory = { status: "loaded", value: action.payload };
      });
  },
});

export const { setDisplayState, setIsFullScreen, handleAgentMessage, resetSession, setSideBarOpen } =
  chatSlice.actions;

function handleAgentMessageImpl(state: IChatState, payload: NewAgentMessageProps) {
  const sessionState = state.session;
  if (sessionState.messages.status !== "loaded") {
    console.error("Cannot handle agent message when messages is in error or loading state");
    return;
  }
  handleReasoningSummary(payload, sessionState);
  switch (payload.message.type) {
    case "new_conversation":
      sessionState.conversationId = payload.message.conversationId;
      if (state.conversationsHistory.status === "loaded") {
        state.conversationsHistory.value.push({
          id: payload.message.conversationId,
          subject: undefined,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        });
      }
      break;

    case "tool_call_in_progress":
      sessionState.toolCallInProgress = { status: true, toolName: payload.message.toolName };
      if (payload.message.toolName === PythonInterpreterToolName) {
        sessionState.messages.value.push({
          id: nanoid(),
          origin: "bot",
          content: payload.message.arguments.code as string,
          timestamp: payload.timestamp,
          messageType: "code_interpreter",
        });
      }
      break;
    case "tool_call_failed":
    case "tool_call_success":
      sessionState.toolCallInProgress = { status: false };
      break;
    case "delta": {
      sessionState.partialResponse += payload.message.content;
      sessionState.toolCallInProgress = { status: false };
      break;
    }
    case "done": {
      if (sessionState.partialResponse && !sessionState.partialResponse.endsWith("\n")) {
        sessionState.partialResponse += "  \n\n";
      }
      break;
    }
    case "final": {
      completeAgentResponse(sessionState, payload.timestamp);
      break;
    }
    case "charts.line_chart": {
      completeAgentResponse(sessionState, payload.timestamp);
      sessionState.messages.value.push({
        id: nanoid(),
        origin: "bot",
        content: convertServerChartToClientFormat({
          type: "charts.line_chart",
          title: payload.message.title || "Chart",
          lines: payload.message.lines || [],
        }),
        timestamp: payload.timestamp,
        messageType: "chart",
      });
      break;
    }
    case "charts.bar_chart": {
      completeAgentResponse(sessionState, payload.timestamp);
      sessionState.messages.value.push({
        id: nanoid(),
        origin: "bot",
        content: convertServerChartToClientFormat({
          type: "charts.bar_chart",
          title: payload.message.title || "Chart",
          bars: payload.message.bars || [],
        }),
        timestamp: payload.timestamp,
        messageType: "chart",
      });
      break;
    }
    case "unhandled_error":
      completeAgentResponse(sessionState, payload.timestamp);
      sessionState.messages.value.push({
        id: nanoid(),
        origin: "bot",
        content: errorMessages.unhandledError,
        timestamp: payload.timestamp,
        messageType: "text",
      });
      break;
    case "too_many_prompts_in_conversation":
      completeAgentResponse(sessionState, payload.timestamp);
      sessionState.messages.value.push({
        id: nanoid(),
        origin: "bot",
        content: errorMessages.tooManyRequests,
        timestamp: payload.timestamp,
        messageType: "text",
      });
      sessionState.isSessionLocked = true;
      break;
    case "suggestions": {
      completeAgentResponse(sessionState, payload.timestamp);
      const lastMessage = sessionState.messages.value.at(-1);
      if (lastMessage?.messageType === "text" && lastMessage.origin === "bot") {
        lastMessage.suggestions = payload.message.suggestions;
      } else {
        console.error(
          "Cannot attach suggestions to last message because it is not a bot text message",
          lastMessage
        );
      }
      break;
    }
  }
}

export const getConversationHistory = createAsyncThunk(
  "chat/getConversationHistory",
  async (
    {
      conversationId,
    }: {
      conversationId: string;
    },
    { signal }
  ) => {
    const history = await api.getConversationHistory(conversationId, signal);
    return { conversationId: conversationId, ...history };
  }
);

export const getConversationsList = createAsyncThunk(
  "chat/getConversationsList",
  async (_, { signal }) => {
    return await api.getConversationsList(signal);
  }
);

export const deleteConversation = createAsyncThunk(
  "chat/deleteConversation",
  async (
    {
      conversationId,
    }: {
      conversationId: string;
    },
    { signal }
  ) => {
    await api.deleteConversation(conversationId, signal);
    return await api.getConversationsList(signal);
  }
);

export const postUserMessage = createAsyncThunk(
  "chat/postUserMessage",
  async (
    {
      conversationId,
      userMessage,
    }: {
      conversationId?: string;
      userMessage: string;
    },
    { dispatch, signal }
  ) => {
    dispatch(chatSlice.actions.postUserMessage(userMessage));
    await api.postMessage(
      { prompt: userMessage, conversationId },
      (message) => {
        const parsedMessage = JSON.parse(message) as AgentMessage;
        if (parsedMessage.type === "conversation_subject_updated") {
          dispatch(getConversationsList());
        } else {
          dispatch(
            handleAgentMessage({
              conversationId,
              message: parsedMessage,
              timestamp: Date.now(),
            })
          );
        }
      },
      signal
    );
  }
);

function completeAgentResponse(sessionState: ISessionState, timestamp: number) {
  if (sessionState.messages.status !== "loaded") {
    console.error("Cannot handle agent message when messages is in error or loading state");
    return;
  }
  if (sessionState.partialResponse) {
    sessionState.messages.value.push({
      id: nanoid(),
      origin: "bot",
      content: sessionState.partialResponse,
      timestamp: timestamp,
      messageType: "text",
    });
  }
  sessionState.partialResponse = "";
  sessionState.isAgentReplyInProgress = false;
  sessionState.toolCallInProgress = { status: false };
}

function handleReasoningSummary(payload: NewAgentMessageProps, sessionState: ISessionState) {
  if (sessionState.messages.status !== "loaded") {
    console.error("Cannot handle agent message when messages is in error or loading state");
    return;
  }
  if (payload.message.type === "reasoning_summary_delta") {
    if (sessionState.reasoningSummaryIndex !== payload.message.summaryIndex) {
      sessionState.reasoningSummaryIndex = payload.message.summaryIndex;
      if (sessionState.partialReasoning) {
        sessionState.partialReasoning += "\\\n\\\n";
      }
    }
    sessionState.partialReasoning += payload.message.content;
  } else {
    sessionState.reasoningSummaryIndex = -1;
    // if there is a reasoning summary pending, and the current message is text delta,
    // flush the reasoning so that it appears before the text and new reasoning summary will appear later
    if (sessionState.partialReasoning && payload.message.type === "delta") {
      sessionState.messages.value.push({
        id: nanoid(),
        origin: "bot",
        content: sessionState.partialReasoning,
        timestamp: payload.timestamp,
        messageType: "reasoning",
      });
      sessionState.partialReasoning = "";
    }
  }
}

export const chatReducer = chatSlice.reducer;
